import { useState } from "react";
import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { authPageMeta } from "data/authPages.enum";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { selectAuthLoading } from "store/slices/cognitoAuthSlice";
import { validatePassword } from "utils/passwordValidator";
import LucideIcon from "utils/LucideIcon";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { IAccountChangePass } from "Interfaces/interface.auth";
import { changePassword } from "store/slices/authSlice";

const PageChangePassword = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("password");
  const [newPassword, setNewPassword] = useState("password");
  const [confirmPassword, setConfirmPassword] = useState("password");

  const validate = (values: IAccountChangePass) => {
    const errors: Partial<IAccountChangePass> = {};

    // Current Password
    if (!values.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    // New Password
    const passwordError = validatePassword(values.newPassword);
    if (passwordError) {
      errors.newPassword = passwordError;
    }

    // Confirm Password
    if (!values.confirmPassword) {
      errors.confirmPassword = "Confirm Password is required";
    } else if (values.confirmPassword !== values.newPassword) {
      errors.confirmPassword = "Passwords must match";
    }

    return errors;
  };

  const handleSubmit = async (
    values: IAccountChangePass,
    { setSubmitting, resetForm }: FormikHelpers<IAccountChangePass>
  ) => {
    try {
      const result = await dispatch(
        changePassword({
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
        })
      ).unwrap();
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      resetForm();
    } catch (err: any) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout {...authPageMeta.change}>
      <div className="flex items-center">
        <div className="w-full flex justify-center items-center">
          <motion.div
            className="bg-white dark:bg-neutral-800 rounded-2xl w-full"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
          >
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6 text-center font-display">
              Change Password
            </h2>

            <Formik
              initialValues={{
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
              }}
              validate={validate}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className="space-y-6">
                  <div>
                    <label
                      htmlFor="currentPassword"
                      className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs mb-1"
                    >
                      Current password
                    </label>
                    <div className="relative">
                      <Field
                        type={currentPassword}
                        id="currentPassword"
                        name="currentPassword"
                        placeholder="Current Password"
                        className={`mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${
                          errors.currentPassword && touched.currentPassword
                            ? "border-red-500"
                            : ""
                        }`}
                        disabled={isLoading || isSubmitting}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body ">
                        {currentPassword === "password" && (
                          <LucideIcon
                            name="EyeOff"
                            className="p-1 cursor-pointer"
                            onClick={() => setCurrentPassword("text")}
                          />
                        )}
                        {currentPassword === "text" && (
                          <LucideIcon
                            name="Eye"
                            className="p-1 cursor-pointer"
                            onClick={() => setCurrentPassword("password")}
                          />
                        )}
                      </div>
                    </div>
                    <ErrorMessage
                      name="currentPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs mb-1"
                    >
                      New password
                    </label>
                    <div className="relative">
                      <Field
                        type={newPassword}
                        id="newPassword"
                        name="newPassword"
                        placeholder="New Password"
                        className={`mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${
                          errors.newPassword && touched.newPassword
                            ? "border-red-500"
                            : ""
                        }`}
                        disabled={isLoading || isSubmitting}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body ">
                        {newPassword === "password" && (
                          <LucideIcon
                            name="EyeOff"
                            className="p-1 cursor-pointer"
                            onClick={() => setNewPassword("text")}
                          />
                        )}
                        {newPassword === "text" && (
                          <LucideIcon
                            name="Eye"
                            className="p-1 cursor-pointer"
                            onClick={() => setNewPassword("password")}
                          />
                        )}
                      </div>
                    </div>
                    <ErrorMessage
                      name="newPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-neutral-800 dark:text-neutral-200 font-medium font-body text-xs mb-1"
                    >
                      Confirm password
                    </label>
                    <div className="relative">
                      <Field
                        type={confirmPassword}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm New Password"
                        className={`mt-1 block w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-sm font-normal h-10 px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 dark:focus:ring-primary-600 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${
                          errors.confirmPassword && touched.confirmPassword
                            ? "border-red-500"
                            : ""
                        }`}
                        disabled={isLoading || isSubmitting}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body ">
                        {confirmPassword === "password" && (
                          <LucideIcon
                            name="EyeOff"
                            className="p-1 cursor-pointer"
                            onClick={() => setConfirmPassword("text")}
                          />
                        )}
                        {confirmPassword === "text" && (
                          <LucideIcon
                            name="Eye"
                            className="p-1 cursor-pointer"
                            onClick={() => setConfirmPassword("password")}
                          />
                        )}
                      </div>
                    </div>
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <motion.div className="flex flex-col gap-2 justify-center items-center">
                    <ButtonPrimary
                      type="submit"
                      loading={isLoading || isSubmitting}
                      className="w-full text-white font-semibold py-[14px] rounded-xl shadow-md transition-colors duration-300 hover:scale-105 ease-in text-sm disabled:cursor-not-allowed disabled:text-gray-800 mt-2"
                    >
                      {!isSubmitting && "Update Password"}
                    </ButtonPrimary>
                    <Link
                      to="/forgot-password"
                      className={`text-primary-500 hover:text-primary-600 text-xs font-body ml-3
                        ${
                          (isLoading || isSubmitting) &&
                          "pointer-events-none opacity-50"
                        }
                        `}
                    >
                      Forgot password?
                    </Link>
                  </motion.div>
                  <div className="text-center mt-4">
                    <Link
                      type="button"
                      to="/profile"
                      className={`text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300
                        ${
                          (isLoading || isSubmitting) &&
                          "pointer-events-none opacity-50"
                        }
                        `}
                    >
                      Back to Profile
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          </motion.div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default PageChangePassword;
